import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurationService, CoursesService, PathsService, PortalConfigurationService, RestApiService, UserManagementService } from '@draeger-academy/common';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { OrderService } from '@draeger-academy/common/lib/services/order/order.service';
import { FormsUtils } from '@draeger-academy/presentation/lib/modules/form-builder';
import { Order } from 'models/order';
import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { GenericModalService } from '../../generic-modal';

@Component({
    selector: 'da-booking-person',
    templateUrl: './booking-person.component.html',
    styleUrls: ['./booking-person.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BookingPersonComponent implements OnInit, OnDestroy {
    @Input() activeStep: number;
    @Output()
    nextStep: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    loggedIn: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('loginPopupShow', { static: true }) private _loginPopupShow;
    private _modalId: string;
    public booking: UntypedFormGroup;
    public hasFocusNote = false;
    public hasPONumber = false;
    public order: Order = new Order([]);
    private _$loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showLogin: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showLoginData: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showLoginForm: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _subscriptionLoggedIn: Subscription;
    private _order: Subscription;
    public regionKey = '';
    showBillingContactForm: boolean;
    @Input() showLogin = true;
    public targetUrl: string;
    private _currentData = null;
    public showSameAsBookingContactCheckbox = true;
    public site: any;
    public showBilling: boolean;
    public isLoggedIn = false;
    public isPortal: boolean;
    public billingFormWasEdited = false;
    public billingForm: any;
    public billing: any;
    public translatedSalutationUser: string;
    public translatedSalutationBilling: string;
    public translatedCountryUser: string;
    public translatedCountryBilling: string;
    public preselectedCountry: string;
    public salutationData: any;
    public countriesData: any;
    public portalId: string;
    public submitted = false;
    public countryInfo1Key: string;
    public countryInfo1Label: string;
    public countryInfo2Key: string;
    public countryInfo2Label: string;
    public hasFocusCountryInfo1 = false;
    public hasFocusCountryInfo2 = false;

    get hasCountryInfo1(): boolean {
        return !!this.countryInfo1Key && !!this.countryInfo1Label;
    }

    get hasCountryInfo2(): boolean {
        return !!this.countryInfo2Key && !!this.countryInfo2Label;
    }

    public get loading$(): Observable<boolean> {
        return this._$loading.asObservable();
    }

    public get showLogin$(): Observable<boolean> {
        return this._$showLogin.asObservable();
    }

    public get showLoginData$(): Observable<boolean> {
        return this._$showLoginData.asObservable();
    }

    public get showLoginForm$(): Observable<boolean> {
        return this._$showLoginForm.asObservable();
    }

    private _$order: BehaviorSubject<any> = new BehaviorSubject([]);
    public get order$(): Observable<Order> {
        return this._$order.asObservable();
    }

    private _$errorMessage: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get errorMessage$(): Observable<boolean> {
        return this._$errorMessage.asObservable();
    }

    private _$billingFormWasEdited: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get billingFormWasEdited$(): Observable<boolean> {
        return this._$billingFormWasEdited.asObservable();
    }

    private _$loginPopupShow: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get loginPopupShow$(): Observable<boolean> {
        return this._$loginPopupShow.asObservable();
    }

    eventsSubject: Subject<void> = new Subject<void>();
    private _$user: BehaviorSubject<any> = new BehaviorSubject(false);
    linkPersonalData: string;
    public get user$(): Observable<any> {
        return this._$user.asObservable();
    }

    private _$billingContact: BehaviorSubject<any> = new BehaviorSubject(false);
    public get billingContact$(): Observable<any> {
        return this._$billingContact.asObservable();
    }

    private _$hasRegions: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get hasRegions$(): Observable<boolean> {
        return this._$hasRegions.asObservable();
    }

    private _$userData: BehaviorSubject<any> = new BehaviorSubject(false);
    public get userData$(): Observable<any> {
        return this._$userData.asObservable();
    }

    private _$loggedInUserShowBillingContactForm: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get loggedInUserShowBillingContactForm$(): Observable<boolean> {
        return this._$loggedInUserShowBillingContactForm.asObservable();
    }

    emitEventToChild() {
        this.eventsSubject.next();
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private authenticationService: AuthenticationService,
        private _configurationService: ConfigurationService,
        private userService: UserManagementService,
        private orderService: OrderService,
        private _cartService: CartService,
        private _pathsService: PathsService,
        private _genericModalService: GenericModalService,
        private _portalConfigurationService: PortalConfigurationService,
        public _restApi: RestApiService,
        public _courseService: CoursesService
    ) {
        this.isPortal = this._configurationService.isPartnerPage();

        if (!this.isPortal) {
            this.site = this._configurationService.getCurrentSite();
        } else {
            this.site = null;
            const portal = JSON.parse(sessionStorage.getItem('portalLang'));
            this.portalId = portal.portalId
        }
    }

    ngOnInit() {
        this._courseService.languageSelected.subscribe((language) => {
            this.getUserAndBillingContact(true);
        });
        this.getUserAndBillingContact(true);
        this.isPortal = this._configurationService.isPartnerPage();
        if (this.isPortal) {
            const portalPreferences = this._portalConfigurationService.$portalPreferences.getValue();
            this.countryInfo1Key = portalPreferences.countryInfo1Key;
            this.countryInfo1Label = portalPreferences.countryInfo1Label;
            this.countryInfo2Key = portalPreferences.countryInfo2Key;
            this.countryInfo2Label = portalPreferences.countryInfo2Label;
        }
        this.checkIfShowBilling();
        if (this._configurationService.isBillingContactEdited === true) {
            this._$billingFormWasEdited.next(true);
        } else {
            this._$billingFormWasEdited.next(false);
        }
        this.linkPersonalData = this._pathsService.getAccountPersonalDataLink();
        this.targetUrl = this._pathsService.getCheckoutLink();
        const patternForNote = /^[\p{L}\p{N}\s.,;:!?'"()\-@#&%€$*+=\/\\]*$/u;
        const formData = {
            note: ['', Validators.pattern(patternForNote)],
            number: ['', [Validators.maxLength(50), Validators.pattern(patternForNote)]],
            person: [''],
            sameAsBookingContact: !this.showBilling,
            billing: ['']
        };
        if (this.hasCountryInfo1) {
            formData['countryInfo1Key'] = [this.countryInfo1Key];
            formData['countryInfo1Value'] = ['', Validators.pattern(patternForNote)];
        }        
        if (this.hasCountryInfo2) {
            formData['countryInfo2Key'] = [this.countryInfo2Key];
            formData['countryInfo2Value'] = ['', Validators.pattern(patternForNote)];
        }

        this.booking = this.formBuilder.group(formData);        
        
        this._order = this.orderService.order$.pipe(take(1)).subscribe((order) => {
            const user = this.booking.get('person');
            const note = this.booking.get('note');
            this.billing = this.booking.get('billing');
            const POnumber = this.booking.get('number');

            if (order) {
                if (this.hasCountryInfo1 && order['countryInfo1Value']) {
                    this.booking.get('countryInfo1Key').setValue(order['countryInfo1Key']);
                    this.booking.get('countryInfo1Value').setValue(order['countryInfo1Value']);
                }
                if (this.hasCountryInfo2 && order['countryInfo2Value']) {
                    this.booking.get('countryInfo2Key').setValue(order['countryInfo2Key']);
                    this.booking.get('countryInfo2Value').setValue(order['countryInfo2Value']);
                }
                if (order.billing) {
                    this.showBilling = true;
                    this.showBillingContactForm = true;
                }
                order.requestCreated = true;
                this.order = order;
                if (order && order.user) {
                    const newPerson = {
                        address: this.order.user.address,
                        houseNumber: this.order.user.houseNumber,
                        city: this.order.user.city,
                        company: this._updateFormat(this.order.user.company),
                        regionKey: this.order.user.regionKey,
                        country: this._updateFormat(this.order.user.country),
                        department: this.order.user.department,
                        email: this.order.user.email,
                        firstName: this.order.user.firstName,
                        lastName: this.order.user.lastName,
                        phone: this.order.user.phone,
                        salutation: this._updateFormat(this.order.user.salutation),
                        subscribe: this.order.user.subscribe,
                        title: this.order.user.title,
                        zipCode: this.order.user.zipCode
                    };

                    this._$user.next(newPerson);
                    user.setValue(newPerson);

                    note.setValue(order.user.note);
                    POnumber.setValue(order.user.POnumber);

                    if (!this.isPortal) {
                        this._configurationService.$siteSettings.subscribe((data) => {
                            if (data && data.length > 0) {
                                this._currentData = data;
                                const _regionsData = data.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                if (_regionsData) {
                                    const region = _regionsData['value'].filter((item) => item === order.user.regionKey);
                                    if (region && region[0]) {
                                        this.regionKey = region[0];
                                        this._$hasRegions.next(true);
                                    }
                                }
                            }
                        });
                    } else {
                        this._configurationService.getConfigPartnerByPortal().then((values) => {
                            if (values) {
                                const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                if (_regionsData) {
                                    const region = _regionsData['value'].filter((item) => item === order.user.regionKey);
                                    if (region && region[0]) {
                                        this.regionKey = region[0];
                                        this._$hasRegions.next(true);
                                    }
                                }
                            }
                        });
                    }
                } else {
                    this.getUserAndBillingContact(false);
                }

                if (order && order.billing) {
                    if (JSON.stringify(order.billing) === JSON.stringify(order.user)) {
                        this.f.sameAsBookingContact.setValue(true);
                        this.f.billing.setValue(this.order.user);
                        this._$billingContact.next(order.billing);
                    } else {
                        this.f.sameAsBookingContact.setValue(false);
                        this.f.billing.setValue(this.order.billing);
                        this._$billingContact.next(order.billing);
                    }
                }

                this._subscriptionLoggedIn = this.authenticationService.loggedIn().subscribe((data) => {
                    if (this._configurationService.loggedInByPopup$ === true) {
                        this._configurationService.updateIfBillingContactEdited(false);
                        this.showBilling = false;
                        this.showBillingContactForm = false;
                        this._$billingFormWasEdited.next(false);
                        this.f.sameAsBookingContact.setValue(true);
                        this.order.user = null;
                        this.order.billing = null;
                        this.orderService.updateOrder(this.order);
                        if (data) {
                            this._$loading.next(true);
                            this.userService.user$.subscribe((user) => {
                                if (user) {
                                    this.translateSalutationsAndCountries(user);
                                    this._$userData.next(user);
                                    this._$showLoginData.next(true);
                                    this._$showLoginForm.next(false);
                                    this.isLoggedIn = true;
                                    this.f.sameAsBookingContact.setValue(false);
                                    this.showSameAsBookingContactCheckbox = false;
                                    const newPerson = {
                                        id: user.id,
                                        billingContactId: user.billingContactId,
                                        address: user.address,
                                        houseNumber: user.houseNumber,
                                        city: user.city,
                                        company: user.company && user.company.name ? user.company.name : user.company,
                                        regionKey: user.regionKey,
                                        country: user.country.code,
                                        department: user.department,
                                        email: user.email,
                                        firstName: user.firstName,
                                        lastName: user.lastName,
                                        phone: user.phone,
                                        salutation: this._updateFormat(user.salutation),
                                        subscribe: user.subscribe,
                                        title: user.title,
                                        zipCode: user.zipCode
                                    };

                                    this._$user.next(newPerson);
                                    const person = this.booking.get('person');
                                    person.setValue(user);

                                    const billingObj = {
                                        address: user.billingContact.address,
                                        houseNumber: user.billingContact.houseNumber,
                                        city: user.billingContact.city,
                                        company: this._updateFormat(user.billingContact.company),
                                        regionKey: user.billingContact.regionKey,
                                        country: this._updateFormat(user.billingContact.country),
                                        department: user.billingContact.department,
                                        email: user.billingContact.email,
                                        firstName: user.billingContact.firstName,
                                        lastName: user.billingContact.lastName,
                                        phone: user.billingContact.phone,
                                        salutation: this._updateFormat(user.billingContact.salutation),
                                        title: user.billingContact.title,
                                        zipCode: user.billingContact.zipCode
                                    };
                                    this.booking.get('billing').setValue(billingObj);
                                    setTimeout(() => {    
                                        this.billing.patchValue({
                                            address: user.billingContact.address,
                                            houseNumber: user.billingContact.houseNumber,
                                            city: user.billingContact.city,
                                            company: this._updateFormat(user.billingContact.company),
                                            regionKey: user.billingContact.regionKey,
                                            country: this._updateFormat(user.billingContact.country),
                                            department: user.billingContact.department,
                                            email: user.billingContact.email,
                                            firstName: user.billingContact.firstName,
                                            lastName: user.billingContact.lastName,
                                            phone: user.billingContact.phone,
                                            salutation: this._updateFormat(user.billingContact.salutation),
                                            title: user.billingContact.title,
                                            zipCode: user.billingContact.zipCode
                                        });
                                        if (user.billingContactId === user.id && !this._$billingFormWasEdited.getValue()) {
                                            this.setFormValue(user);
                                            this._$billingContact.next(user);
                                        } else if (user.billingContactId && !this._$billingFormWasEdited.getValue()) {
                                            this._$loading.next(true);
                                            this.userService.getBillingContact(user.billingContactId).then((useR) => {
                                                if (useR) {
                                                    this.setFormValue(useR);
                                                    this._$billingContact.next(useR);    
                                                }
                                                this._$loading.next(false);
                                            });
                                        } else if (this._$billingFormWasEdited.getValue()) {
                                            this.billing = this.booking.get('billing');
                                            const billingForm = this.booking.controls.billing.value;

                                            if (this.billing) {
                                                this.billing.patchValue({
                                                    address: billingForm.address,
                                                    houseNumber: billingForm.houseNumber,
                                                    company: this._updateFormat(billingForm.company),
                                                    zipCode: billingForm.zipCode,
                                                    city: billingForm.city,
                                                    firstName: billingForm.firstName,
                                                    lastName: billingForm.lastName,
                                                    phone: billingForm.phone,
                                                    email: billingForm.email,
                                                    department: billingForm.department,
                                                    salutation: this._updateFormat(billingForm.salutation),
                                                    title: billingForm.title,
                                                    regionKey: billingForm.regionKey,
                                                    country: this._updateFormat(billingForm.country)
                                                });
                                            }
                                        }
    
                                        this._$showLogin.next(false);
                                        if (user) {
                                            this._$loggedInUserShowBillingContactForm.next(false);
                                        }
    
                                        if (!this.isPortal) {
                                            this._configurationService.$siteSettings.subscribe((datas) => {
                                                if (datas && datas.length > 0) {
                                                    this._currentData = datas;
                                                    const _regionsData = datas.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                                    if (_regionsData) {
                                                        const region = _regionsData['value'].filter((item) => item === user.regionKey);
                                                        if (region && region[0]) {
                                                            this.regionKey = region[0];
                                                            this._$hasRegions.next(true);
                                                        }
                                                    }
                                                }
                                            });
                                        } else {
                                            this._configurationService.getConfigPartnerByPortal().then((values) => {
                                                if (values) {
                                                    const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                                    if (_regionsData) {
                                                        const region = _regionsData['value'].filter((item) => item === user.regionKey);
                                                        if (region && region[0]) {
                                                            this.regionKey = region[0];
                                                            this._$hasRegions.next(true);
                                                        }
                                                    }
                                                }
                                            });
                                        }                                        
                                        this._$loading.next(false);
                                    }, 1000);
                                } else {
                                    if (this.booking) {
                                        const useR = this.booking.get('person');
                                        const note = this.booking.get('note');
                                        this.billing = this.booking.get('billing');
                                        const POnumber = this.booking.get('number');

                                        useR.setValue(null);
                                        note.setValue(null);
                                        this.billing.setValue(null);
                                        POnumber.setValue(null);
                                    }
                                    this._$loading.next(false);
                                }
                            });
                        } else {
                            this._$showLoginData.next(false);
                            this._$showLoginForm.next(true);
                            const showLogin = localStorage.getItem('showLogin') === 'true';
                            if (showLogin) {
                                this._$showLogin.next(this._configurationService.hasFeature('Login') && showLogin);
                            } else {
                                this._$showLogin.next(showLogin);
                            }
                        }
                    } else {
                        if (data) {
                            this.isLoggedIn = true;
                            this._$showLoginData.next(true);
                            this._$showLoginForm.next(false);
                            this.showSameAsBookingContactCheckbox = false;
                        } else {
                            this.isLoggedIn = false;
                            this._$showLoginData.next(false);
                            this._$showLoginForm.next(true);
                            if (order && order.billing) {
                                if (JSON.stringify(order.billing) === JSON.stringify(order.user)) {
                                    this.f.sameAsBookingContact.setValue(true);
                                    this.f.billing.setValue(this.order.user);
                                    this._configurationService.updateIfBillingContactEdited(false);
                                    this._$billingFormWasEdited.next(false);
                                } else {
                                    this.f.sameAsBookingContact.setValue(false);
                                    this.f.billing.setValue(this.order.billing);
                                    this._configurationService.updateIfBillingContactEdited(true);
                                    this._$billingFormWasEdited.next(true);
                                }
                            }
                        }
                    }
                });
                setTimeout(() => {
                    this.billingForm = document.getElementById('billing-contact');
                    if (this.billingForm) {
                        if (this.billingForm.style.maxHeight) {
                            this.billingForm.style.maxHeight = null;
                        } else {
                            this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 'px';
                        }
                    }
                }, 50);
                this._$order.next(order);
                this.orderService.updateOrder(this.order);
            }
        });

        if (this.showBilling) {
            this.showBillingContactForm = true;
            if (this.isLoggedIn && this.site === 'de_de') {
                this.showBillingContactForm = false;
            }
        } else {
            this.showBillingContactForm = false;
        }
        if (this._configurationService.isBillingContactEdited === true) {
            this._$billingFormWasEdited.next(true);
        } else {
            this._$billingFormWasEdited.next(false);
        }

        this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {
                this.userService.loadUser();
            }
        });
    }

    private _updateFormat(value: any): string {
        if (value?.name) {
            return value.name;
        }
        return value;
    }

    public getUserAndBillingContact(login: boolean) {
        this._subscriptionLoggedIn = this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {                
                this._$loading.next(true);
                this.userService.user$.subscribe((user) => {
                    if (user) {
                        this.translateSalutationsAndCountries(user);
                        this._$userData.next(user);
                        this._$showLoginData.next(true);
                        this._$showLoginForm.next(false);
                        this.f.sameAsBookingContact.setValue(false);
                        const newPerson = {
                            id: user.id,
                            billingContactId: user.billingContactId,
                            address: user.address,
                            houseNumber: user.houseNumber,
                            city: user.city,
                            company: user.company && user.company.name ? user.company.name : user.company,
                            regionKey: user.regionKey,
                            country: user.country.code,
                            department: user.department,
                            email: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            phone: user.phone,
                            salutation: typeof user.salutation === 'string' ? user.salutation : user.salutation.name,
                            subscribe: user.subscribe,
                            title: user.title,
                            zipCode: user.zipCode
                        };

                        this._$user.next(newPerson);
                        const person = this.booking.get('person');
                        person.setValue(user);

                        if (user.billingContactId === user.id && !this._$billingFormWasEdited.getValue()) {
                            this.setFormValue(user);
                            this._$billingContact.next(user);
                        } else if (user.billingContactId && !this._$billingFormWasEdited.getValue()) {
                            this.userService.getBillingContact(user.billingContactId).then((useR) => {
                                if (useR) {
                                    this.setFormValue(useR);
                                    this._$billingContact.next(useR);
                                }
                            });
                        } else if (this._$billingFormWasEdited.getValue()) {
                            this.billing = this.booking.get('billing');
                            const billingForm = this.booking.controls.billing.value;

                            if (this.billing) {
                                this.billing.patchValue({
                                    address: billingForm.address,
                                    houseNumber: billingForm.houseNumber,
                                    company: this._updateFormat(billingForm.company),
                                    zipCode: billingForm.zipCode,
                                    city: billingForm.city,
                                    firstName: billingForm.firstName,
                                    lastName: billingForm.lastName,
                                    email: billingForm.email,
                                    department: billingForm.department,
                                    phone: billingForm.phone,
                                    salutation: this._updateFormat(billingForm.salutation),
                                    title: billingForm.title,
                                    regionKey: billingForm.regionKey,
                                    country: this._updateFormat(billingForm.country)
                                });
                            }
                        }

                        this._$showLogin.next(false);
                        if (user) {
                            this._$loggedInUserShowBillingContactForm.next(false);
                        }

                        if (!this.isPortal) {
                            this._configurationService.$siteSettings.subscribe((datas) => {
                                if (datas && datas.length > 0) {
                                    this._currentData = datas;
                                    const _regionsData = datas.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                    if (_regionsData) {
                                        const region = _regionsData['value'].filter((item) => item === user.regionKey);
                                        if (region && region[0]) {
                                            this.regionKey = region[0];
                                            this._$hasRegions.next(true);
                                        }
                                    }
                                }
                            });
                        } else {
                            this._configurationService.getConfigPartnerByPortal().then((values) => {
                                if (values) {
                                    const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                    if (_regionsData) {
                                        const region = _regionsData['value'].filter((item) => item === user.regionKey);
                                        if (region && region[0]) {
                                            this.regionKey = region[0];
                                            this._$hasRegions.next(true);
                                        }
                                    }
                                }
                            });
                        }
                    } else {
                        if (this.booking) {
                            const useR = this.booking.get('person');
                            const note = this.booking.get('note');
                            this.billing = this.booking.get('billing');
                            const POnumber = this.booking.get('number');

                            useR.setValue(null);
                            note.setValue(null);
                            this.billing.setValue(null);
                            POnumber.setValue(null);
                        }
                    }
                    this._$loading.next(false);
                });
            } else {
                this._$showLoginData.next(false);
                this._$showLoginForm.next(true);
                const showLogin = localStorage.getItem('showLogin') === 'true';
                if (showLogin) {
                    this._$showLogin.next(this._configurationService.hasFeature('Login') && showLogin);
                } else {
                    this._$showLogin.next(showLogin);
                }
            }
        });
    }

    setFormValue(user) {
        this._configurationService.$siteSettings.subscribe((data) => {
            if (data && data.length > 0 && JSON.stringify(this._currentData) !== JSON.stringify(data)) {
                this._currentData = data;
                const _salutationsData = this._configurationService.$allSalutationsByLanguage.getValue();
                this._$billingContact.next(user);
                this.billing = this.booking.get('billing');
                const userObj = {
                    address: user.address,
                    houseNumber: user.houseNumber,
                    city: user.city,
                    company: user.company && user.company.name ? user.company.name : user.company,
                    regionKey: user.regionKey,
                    country: user.country.name,
                    department: user.department,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    phone: user.phone,
                    salutation: user.salutation && user.salutation.code ? user.salutation.code : _salutationsData[0].key,
                    subscribe: user.subscribe,
                    title: user.title,
                    zipCode: user.zipCode
                };
                if (this.billing) {
                    this.billing.setValue(userObj);
                }
            }
        });
    }

    get f() {
        return this.booking?.controls;
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    public isActive(currentStep: number) {
        if (currentStep < Number(this.activeStep)) {
            return true;
        }
    }

    public isCurrent(currentStep: number) {
        if (currentStep === Number(this.activeStep)) {
            return true;
        }
    }

    public sameAs(event) {
        this.billing = this.booking.get('billing');
        const billingForm = this.booking.controls.billing.value;
        const booking = this.booking.get('person');
        const bookingForm = this.booking.controls.person.value;

        this._$user.pipe(take(1)).subscribe((user) => {
            if (user) {
                if (event) {
                    this.billing.patchValue({
                        department: billingForm.department,
                        email: billingForm.email,
                        firstName: billingForm.firstName,
                        lastName: billingForm.lastName,
                        phone: billingForm.phone,
                        salutation: this._updateFormat(billingForm.salutation),
                        subscribe: event,
                        title: billingForm.title,
                        companySameAs: event,
                        address: user.address,
                        houseNumber: user.houseNumber,
                        company: this._updateFormat(user.company),
                        zipCode: user.zipCode,
                        city: user.city,
                        regionKey: user.regionKey,
                        country: this._updateFormat(user.country)
                    });
                } else {
                    this.setCompanyFieldsEmpty(this.billing);
                }
            } else {
                if (event) {
                    if (booking) {
                        this.billing.patchValue({
                            address: bookingForm.address,
                            houseNumber: bookingForm.houseNumber,
                            company: this._updateFormat(bookingForm.company),
                            zipCode: bookingForm.zipCode,
                            city: bookingForm.city,
                            firstName: billingForm.firstName,
                            lastName: billingForm.lastName,
                            email: billingForm.email,
                            department: billingForm.department,
                            phone: billingForm.phone,
                            salutation: this._updateFormat(billingForm.salutation),
                            subscribe: event,
                            title: billingForm.title,
                            companySameAs: event,
                            regionKey: bookingForm.regionKey,
                            country: this._updateFormat(bookingForm.country)
                        });
                    } else {
                        this.setCompanyFieldsEmpty(this.billing);
                    }
                } else {
                    this.setCompanyFieldsEmpty(this.billing);
                }
            }
        });
    }

    setCompanyFieldsEmpty(billing) {
        this._configurationService.getConfigPartnerByPortal().then((values) => {
            if (values) {
                const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
                let region;
                if (_regionsData && _regionsData['value']) {
                    region = _regionsData['value'][0];
                } else {
                    region = '';
                }
                billing.patchValue({
                    address: '',
                    company: '',
                    zipCode: '',
                    city: '',
                    regionKey: region,
                    country: this.preselectedCountry
                });
                if (this.order.billing) {
                    this.order.billing.regionKey = region;
                    this.order.billing.country = this.preselectedCountry;
                    this.orderService.updateOrder(this.order);
                }
            }
        });
    }

    goToStep(stepNumber) {
        if (this.f.sameAsBookingContact.value) {
            this.billing = this.booking.get('billing');
            const person = this.booking.get('person');
            this.billing.setValue(person.value);
        }

        this.authenticationService.loggedIn().subscribe((data) => {
            if (!data) {
                this._$user.next(this.f.person.value);
            }
        });

        if (stepNumber === 2) {
            this.submitted = true;
            this._configurationService.updateloggedInByPopup(false);
            if (this.booking.invalid) {
                this._$errorMessage.next(true);
                this.emitEventToChild();
                setTimeout(() => {
                    const errorMessage = document.querySelector('div.invalid-feedback') as HTMLElement;

                    if (errorMessage) {
                        errorMessage.offsetParent.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 400);
                return;
            } else {
                this._$errorMessage.next(false);
                this.nextStep.emit(stepNumber);
                this._$user.pipe(take(1)).subscribe((user) => {
                    if (user) {
                        this.order$.pipe(take(1)).subscribe((order) => {
                            const userData = this._$userData.getValue();
                            let userCountryCode;
                            if (this._$userData.getValue()) {
                                userCountryCode = this._$userData.getValue().country.code;
                            } else {
                                userCountryCode = user.country;
                            }
                            const newPerson = {
                                id: user.id,
                                billingContactId: user.billingContactId,
                                address: user.address,
                                houseNumber: user.houseNumber,
                                city: user.city,
                                company: this._updateFormat(user.company),
                                regionKey: user.regionKey,
                                country: this._updateFormat(userCountryCode),
                                department: user.department,
                                email: user.email,
                                firstName: user.firstName,
                                lastName: user.lastName,
                                phone: user.phone,
                                salutation: this._updateFormat(user.salutation),
                                subscribe: user.subscribe,
                                title: user.title,
                                zipCode: user.zipCode
                            };

                            this.order = order;
                            this.order.user = newPerson;
                            this.order.user.note = this.f.note.value;
                            this.order.user.POnumber = this.f.number.value;
                            if (this.f.sameAsBookingContact.value) {
                                this.order.billing = this.order.user;
                            } else {
                                const billing = this.booking.get('billing');
                                this.order.billing = billing.value;
                            }

                            if (this.order.participants && this.order.participants[0] && this.order.participants[0].sameAs) {
                                this.order.participants[0].person = newPerson;
                            }
                            this.orderService.updateOrder(this.order);
                        });
                    } else {
                        this.order$.pipe(take(1)).subscribe((order) => {
                            this.order = order;
                            this.order.user = this.f.person.value;
                            if (this.f.sameAsBookingContact.value) {
                                this.order.billing = this.f.person.value;
                            } else {
                                this.order.billing = this.f.billing.value;
                            }
                            this.order.user.note = this.f.note.value;
                            this.order.user.POnumber = this.f.number.value;
                            const newPerson = {
                                address: this.order.user.address,
                                houseNumber: this.order.user.houseNumber,
                                city: this.order.user.city,
                                company: this._updateFormat(this.order.user.company),
                                regionKey: this.order.user.regionKey,
                                country: this._updateFormat(this.order.user.country),
                                department: this.order.user.department,
                                email: this.order.user.email,
                                firstName: this.order.user.firstName,
                                lastName: this.order.user.lastName,
                                phone: this.order.user.phone,
                                salutation: this._updateFormat(this.order.user.salutation),
                                subscribe: this.order.user.subscribe,
                                title: this.order.user.title,
                                zipCode: this.order.user.zipCode
                            };
                            if (this.order.participants && this.order.participants[0] && this.order.participants[0].sameAs) {
                                this.order.participants[0].person = newPerson;
                            }
                            this.orderService.updateOrder(this.order);
                        });
                    }
                });
            }
        } else {
            this._cartService.activeStep(0);
            this.nextStep.emit(stepNumber);
        }
        if (this.hasCountryInfo1) {
            this.order['countryInfo1Key'] = this.f.countryInfo1Key.value;
            this.order['countryInfo1Value'] = this.f.countryInfo1Value.value;
        }
        if (this.hasCountryInfo2) {
            this.order['countryInfo2Key'] = this.f.countryInfo2Key.value;
            this.order['countryInfo2Value'] = this.f.countryInfo2Value.value;
        }
        this.orderService.updateOrder(this.order);
    }

    cancel(checkoutProceed) {
        this._$showLogin.next(false);
        this.loggedIn.emit(false);
        localStorage.setItem('showLogin', 'false');
    }

    sameAsBookingContact(isChecked) {
        if (!this.showBilling) {
            this.showBillingContactForm = !isChecked;
            this.showBilling = true;
            localStorage.setItem('sameBillingLikeBooking', 'false');
            const billingData = {
                address: '',
                city: '',
                company: '',
                regionKey: '',
                country: this.preselectedCountry,
                department: '',
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                salutation: '',
                subscribe: '',
                title: '',
                zipCode: '',
                houseNumber: '',
                companySameAs: false
            };
            this.booking.get('billing').setValue(billingData);
            this.sameAs(false);
            this._$billingFormWasEdited.next(true);
            this._configurationService.updateIfBillingContactEdited(true);
            this.showBillingContactForm = true;
        } else {
            if (isChecked) {
                if (this.site !== 'de_de' || this.isPortal) {
                    this.showBilling = false;
                    this.showBillingContactForm = false;
                    this._$billingFormWasEdited.next(false);
                    this._configurationService.updateIfBillingContactEdited(false);
                }
                const person = this.booking.get('person');
                this.booking.get('billing').setValue(person.value);
                this.sameAs(true);
                this._$billingFormWasEdited.next(false);
                this.showBillingContactForm = false;
                this._configurationService.updateIfBillingContactEdited(false);
                localStorage.setItem('sameBillingLikeBooking', 'true');
            } else {
                const billingData = {
                    address: '',
                    city: '',
                    company: '',
                    regionKey: '',
                    country: this.preselectedCountry,
                    department: '',
                    email: '',
                    firstName: '',
                    lastName: '',
                    phone: '',
                    salutation: '',
                    subscribe: '',
                    title: '',
                    zipCode: '',
                    houseNumber: '',
                    companySameAs: false
                };
                this.booking.get('billing').setValue(billingData);
                this.sameAs(false);
                this.showBillingContactForm = true;
                this._$billingFormWasEdited.next(true);
                this._configurationService.updateIfBillingContactEdited(true);
                localStorage.setItem('sameBillingLikeBooking', 'false');
            }
        }

        this._subscriptionLoggedIn = this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {
                this.showSameAsBookingContactCheckbox = false;
                if (isChecked) {
                    this._$loggedInUserShowBillingContactForm.next(false);
                    this.billing = this.booking.get('billing');
                    const userObj = {
                        address: this.order.user.address,
                        city: this.order.user.city,
                        company: this._updateFormat(this.order.user.company),
                        regionKey: this.order.user.regionKey,
                        country: this._updateFormat(this.order.user.country),
                        department: this.order.user.department,
                        email: this.order.user.email,
                        firstName: this.order.user.firstName,
                        lastName: this.order.user.lastName,
                        phone: this.order.user.phone,
                        salutation: this._updateFormat(this.order.user.salutation),
                        subscribe: this.order.user.subscribe,
                        title: this.order.user.title,
                        zipCode: this.order.user.zipCode,
                        houseNumber: this.order.user.houseNumber
                    };

                    this.billing.setValue(userObj);
                } else {
                    this._$loggedInUserShowBillingContactForm.next(true);
                    this.billing = this.booking.get('billing');
                    this.billing.setValue(null);

                    this.billingForm = document.getElementById('billing-contact');
                    if (this.billingForm) {
                        this.billingForm.style.maxHeight = null;
                    }
                }
                this.billingForm = document.getElementById('billing-contact');

                if (!isChecked && this.billingForm) {
                    if (this.billingForm.style.maxHeight) {
                        this.billingForm.style.maxHeight = null;
                    } else {
                        this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 'px';
                    }

                    setTimeout(() => {
                        if (this.billingForm && this.billingForm.style) {
                            this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 3 + 'px';
                        }
                    }, 50);
                } else {
                    if (!this.showBilling && this.billingForm) {
                        this.billingForm.style.maxHeight = null;
                    }
                }
            } else {
                this.showSameAsBookingContactCheckbox = true;
                this.billingForm = document.getElementById('billing-contact');

                if (!isChecked) {
                    this._$loggedInUserShowBillingContactForm.next(true);
                    this.billing = this.booking.get('billing');
                    this.billing.setValue(null);
                    if (this.billingForm) {
                        if (this.billingForm.style.maxHeight) {
                            this.billingForm.style.maxHeight = null;
                        } else {
                            this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 'px';
                        }
                    }

                    setTimeout(() => {
                        if (this.billingForm && this.billingForm.style) {
                            this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 3 + 'px';
                        }
                    }, 50);
                } else {
                    if (!this.showBilling && this.billingForm) {
                        this.billingForm.style.maxHeight = null;
                    }
                }
            }
        });
    }

    public showBillingContactFormClick() {
        this._$loggedInUserShowBillingContactForm.next(true);
        this.billing = this.booking.get('billing');
        const person = this.booking.get('person');
        const billingForm = this.booking.controls.billing.value;
        this.billing.setValue(null);
        this.f.sameAsBookingContact.setValue(false);
        this.showBillingContactForm = true;
        this._$billingContact.subscribe((user) => {
            if (user) {
                this.billing = this.booking.get('billing');
                this.showSameAsBookingContactCheckbox = false;
                const userObj = {
                    address: user.address,
                    city: user.city,
                    company: user.company && user.company.name ? user.company.name : user.name,
                    regionKey: user.regionKey,
                    country: user.country && user.country.name ? user.country.name : user.name,
                    department: user.department,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    phone: user.phone,
                    salutation: this._updateFormat(user.salutation),
                    subscribe: user.subscribe,
                    title: user.title,
                    zipCode: user.zipCode,
                    houseNumber: user.houseNumber
                };
                this.billing.setValue(userObj);

                setTimeout(() => {
                    this.billingForm = document.getElementById('billing-contact');

                    if (this.billingForm) {
                        if (this.billingForm.style.maxHeight) {
                            this.billingForm.style.maxHeight = null;
                        } else {
                            this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 'px';
                        }
                    }
                }, 50);
            } else {
                this.showSameAsBookingContactCheckbox = true;
                this.billing = this.booking.get('billing');
                this.billing.setValue(null);
                this.billingForm = document.getElementById('billing-contact');

                if (this.billingForm.style.maxHeight) {
                    this.billingForm.style.maxHeight = null;
                } else {
                    this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 'px';
                }

                setTimeout(() => {
                    if (this.billingForm && this.billingForm.style) {
                        this.billingForm.style.maxHeight = this.billingForm.scrollHeight + 3 + 'px';
                    }
                }, 50);
            }
        });
    }

    public openLoginPopup() {
        this._overlayHandler1();
    }
    private async _overlayHandler1(): Promise<void> {
        this._modalId = this._genericModalService.create(this._loginPopupShow);
        this._genericModalService.open(this._modalId, this._loginPopupShow, true, 'ispopup', false, true);
    }
    public closeModal() {
        if (this._modalId) {
            this._genericModalService.close(this._modalId);
        }
        this.showBillingContactForm = false;
        this.getUserAndBillingContact(true);
    }

    public checkIfShowBilling() {
        const sameBillingLikeBooking = JSON.parse(localStorage.getItem('sameBillingLikeBooking'));

        if (!this.isPortal) {
            if (this.site === 'de_de' && !this.isLoggedIn) {
                this.showBilling = true;
            } else {
                this.showBilling = false;
            }
        } else if (sameBillingLikeBooking === 'true' || !sameBillingLikeBooking) {
            this.showBilling = false;
        } else if (sameBillingLikeBooking === 'false') {
            this.showBilling = true;
        }
    }

    private translateSalutationsAndCountries(user) {
        this.salutationData = this._configurationService.$allSalutationsByLanguage.getValue();
        this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();
        let salutation;
        if (user.salutation.code === '' || user.salutation.code === null || !user.salutation.code) {
            salutation = null;
        } else {
            salutation = this.salutationData.filter((item) => item.key === user.salutation.code);
        }
        let salutationBilling;
        if (
            user.billingContact.salutation.code === '' ||
            user.billingContact.salutation.code === null ||
            !user.billingContact.salutation.code
        ) {
            salutationBilling = null;
        } else {
            salutationBilling = this.salutationData.filter((item) => item.key === user.billingContact.salutation.code);
        }
        const country = this.countriesData.filter((item) => item.key === user.country.code);
        const countryBilling = this.countriesData.filter((item) => item.key === user.billingContact.country.code);

        if (salutation !== null) {
            this.translatedSalutationUser = salutation[0].value;
        } else {
            this.translatedSalutationUser = null;
        }
        if (salutationBilling !== null) {
            this.translatedSalutationBilling = salutationBilling[0].value;
        } else {
            this.translatedSalutationBilling = null;
        }
        this.translatedCountryUser = country[0].value;
        this.translatedCountryBilling = countryBilling[0].value;
    }

    ngOnDestroy() {
        this._subscriptionLoggedIn.unsubscribe();
    }

    preselectCountry(preselectedCountry) {
        this.preselectedCountry = preselectedCountry;
    }
}
